/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/ble/bleAdapter.ts
 * Author: SWAN
 * Created: 2025-01-27
 * Description: Web BLE Adapter Implementation
 */

import { i18nKeys } from '@/i18n/i18nKeyGen';
import { I18nError } from '@packages/types/errors';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import {
  SERVICE_UUID_WITH_HYPHENS,
  DATA_CHAR_UUID_WITH_HYPHENS,
} from '@packages/protocols/constants';
import { DeviceFrameRingParser } from '@/connections/deviceFrameRingParser';
import { BaseDeviceAdapter } from '@/connections/baseDeviceAdapter';

// BLE 디바이스 정보 타입
interface BleDeviceInfo {
  name: string;
  address: string;
}

export class WebBleAdapter extends BaseDeviceAdapter {
  protected readonly readTimeoutMs = 5000;
  protected readonly noPendingResponseKey = i18nKeys.config.error_bleNoPendingResponse;
  protected readonly logPrefix = '[BLE]';

  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private isConnected = false;
  private selectedDeviceName: string = '';
  private notificationCount = 0; // DEBUG: notification 카운터

  constructor() {
    super(
      new DeviceFrameRingParser({
        logPrefix: '[BLE]',
        bufferCapacity: 8 * 1024 * 1024,
      })
    );
  }

  protected assertReadable(): void {
    if (!this.characteristic) {
      throw new I18nError(i18nKeys.config.error_bleCharacteristicNotAvailable);
    }
  }

  /* --------------------------------------------------------------
   * MODE CONTROL
   * --------------------------------------------------------------*/
  setSinglePacketMode(): void {
    this.mode = 'single';
    this.frameParser.setMode('single');
    this.packetQueue.clear();
  }

  setStreamingMode(): void {
    this.mode = 'stream';
    this.frameParser.setMode('stream');
    this.notificationCount = 0;
    // 이미 수신된 패킷은 보존 (큐 초기화하지 않음), 버퍼에 남은 프레임만 큐로 옮김
    if (this.frameParser.available() > 0) {
      this.drainBufferIntoQueue();
    }
  }

  async scan(): Promise<BleDeviceInfo[]> {
    // Web BLE API에서는 사용자가 디바이스를 선택하므로 빈 배열 반환
    return [];
  }

  async connect(): Promise<void> {
    // Web BLE에서는 사용자가 직접 디바이스를 선택하므로 이 메서드는 사용하지 않음
    throw new I18nError(i18nKeys.config.error_bleMethodNotSupported);
  }

  async scanAndConnect(
    deviceFilter?: DeviceType,
    targetSerialNumber?: string,
    namePrefix: string = 'BELL'
  ): Promise<void> {
    try {
      // ── 1. 스캔 ── namePrefix로 목록 축소 (광고에 이름이 없는 기기는 목록에 안 뜰 수 있음)
      const selectedDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix }],
        optionalServices: [SERVICE_UUID_WITH_HYPHENS],
      });

      const deviceName = selectedDevice.name || '';
      if (!deviceName) {
        throw new I18nError(i18nKeys.config.error_bleInvalidDevice);
      }

      const deviceNameUpper = deviceName.toUpperCase();
      if (deviceFilter === DeviceType.LITE && !deviceNameUpper.startsWith('BELLLITE_')) {
        throw new I18nError(i18nKeys.config.error_bleLiteDeviceOnly);
      } else if (deviceFilter === DeviceType.MINI && !deviceNameUpper.startsWith('BELLMINI_')) {
        throw new I18nError(i18nKeys.config.error_bleMiniDeviceOnly);
      }

      if (targetSerialNumber) {
        const actualSerial = deviceName.replace(/^BELL(LITE|MINI)_/i, '').toUpperCase();
        const expectedSerial = targetSerialNumber.toUpperCase().replace(/^BELL(LITE|MINI)_/, '');
        if (actualSerial !== expectedSerial) {
          throw new Error(
            `Device serial number mismatch: expected ${expectedSerial}, got ${actualSerial}`
          );
        }
      }

      this.device = selectedDevice;
      this.selectedDeviceName = deviceName;

      // ── 2. GATT 연결 + 서비스/특성 검색 (재시도 포함) ──
      // 일부 디바이스(특히 macOS Chrome)는 GATT 연결 직후 즉시 끊기는 경우가 있어
      // NetworkError/조기 disconnect 발생 시 자동 재시도
      const MAX_ATTEMPTS = 3;
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          await this.connectGattWithTimeout(selectedDevice, attempt);
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          const isRetryable = this.isRetryableConnectError(err);
          console.warn(
            `[BLE] connect attempt ${attempt}/${MAX_ATTEMPTS} failed (retryable=${isRetryable}):`,
            err
          );

          // 다음 시도 전 GATT 정리
          try {
            selectedDevice.gatt?.disconnect();
          } catch {}

          if (!isRetryable || attempt === MAX_ATTEMPTS) {
            throw err;
          }

          // 지수 백오프 (500ms, 1000ms)
          const backoff = 500 * attempt;
          await new Promise(resolve => setTimeout(resolve, backoff));
        }
      }

      if (lastError) throw lastError;
    } catch (error) {
      console.error('[BLE] fail(scanAndConnect):', error);

      // 구체적인 오류 메시지 처리
      if (error instanceof I18nError) {
        // 이미 I18nError로 처리된 경우 그대로 throw
        throw error;
      } else if (error instanceof Error) {
        if (error.name === 'NotFoundError') {
          // NotFoundError는 사용자가 디바이스 선택을 취소했을 때도 발생할 수 있음
          if (error.message.includes('User cancelled')) {
            throw new I18nError(i18nKeys.config.error_bleSelectionCancelled, undefined, error);
          } else {
            throw new I18nError(i18nKeys.config.error_bleDeviceUnsupported, undefined, error);
          }
        } else if (
          error.name === 'NetworkError' ||
          /gatt server is disconnected/i.test(error.message) ||
          /early disconnect detected/i.test(error.message)
        ) {
          throw new I18nError(
            i18nKeys.config.error_bleConnectionFailedCheckDevice,
            undefined,
            error
          );
        } else if (/step timeout:/i.test(error.message)) {
          throw new I18nError(i18nKeys.config.error_bleConnectionTimeout, undefined, error);
        } else if (error.name === 'SecurityError') {
          throw new I18nError(i18nKeys.config.error_blePermissionDenied, undefined, error);
        } else {
          throw new I18nError(i18nKeys.config.error_bleUnknownError, undefined, error);
        }
      } else {
        throw new I18nError(i18nKeys.config.error_bleUnknownError);
      }
    }
  }

  /**
   * 단일 GATT 연결 시도 (서비스/특성 검색 + 알림 시작 포함)
   * - 단계별 짧은 타임아웃으로 hang 감지 (전체 ~15초)
   * - gattserverdisconnected 이벤트로 조기 disconnect 즉시 감지
   */
  private async connectGattWithTimeout(
    selectedDevice: BluetoothDevice,
    attempt: number
  ): Promise<void> {
    const STEP_TIMEOUT_MS = 15000;

    // 조기 disconnect 감지를 위한 deferred
    let disconnectReject: ((err: Error) => void) | null = null;
    const disconnectPromise = new Promise<never>((_, reject) => {
      disconnectReject = reject;
    });
    const onDisconnect = () => {
      console.warn(`[BLE] gattserverdisconnected fired during connect (attempt ${attempt})`);
      disconnectReject?.(
        new Error('GATT server disconnected during connect (early disconnect detected)')
      );
    };
    selectedDevice.addEventListener('gattserverdisconnected', onDisconnect);

    const withTimeout = <T>(label: string, p: Promise<T>): Promise<T> => {
      let timeoutId: NodeJS.Timeout | null = null;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`Step timeout: ${label} (>${STEP_TIMEOUT_MS}ms)`)),
          STEP_TIMEOUT_MS
        );
      });
      return Promise.race([p, disconnectPromise, timeoutPromise]).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
      }) as Promise<T>;
    };

    try {
      const server = await withTimeout('gatt.connect', selectedDevice.gatt!.connect());
      if (!server) throw new I18nError(i18nKeys.config.error_bleGattServerFailed);

      const service = await withTimeout(
        'getPrimaryService',
        server.getPrimaryService(SERVICE_UUID_WITH_HYPHENS)
      );

      const characteristic = await withTimeout(
        'getCharacteristic',
        service.getCharacteristic(DATA_CHAR_UUID_WITH_HYPHENS)
      );

      this.characteristic = characteristic;

      // MTU 협상 (백그라운드, 실패 무시)
      if ('requestMTU' in server && typeof (server as any).requestMTU === 'function') {
        (server as any).requestMTU(512).catch(() => {});
      }

      await withTimeout('startNotifications', characteristic.startNotifications());

      characteristic.addEventListener(
        'characteristicvaluechanged',
        this.handleNotification.bind(this)
      );

      this.frameParser.clear();
      this.pendingPromise = null;
      this.pendingResolver = null;
      this.packetQueue.clear();
      this.mode = 'single';
      this.frameParser.setMode('single');
      this.isConnected = true;
    } finally {
      // 연결 단계의 일시적 disconnect 감지 리스너 제거
      // (실제 사용 중 disconnect는 별도로 처리됨)
      selectedDevice.removeEventListener('gattserverdisconnected', onDisconnect);
    }
  }

  /**
   * 연결 중 발생한 에러가 재시도 가능한지 판단
   * - NetworkError: GATT 조기 disconnect (가장 흔한 재시도 케이스)
   * - 단계별 타임아웃: 디바이스 응답 없음
   * - "GATT server disconnected" 메시지 포함: 조기 disconnect
   */
  private isRetryableConnectError(err: unknown): boolean {
    if (!(err instanceof Error)) return false;
    if (err.name === 'NetworkError') return true;
    const msg = err.message || '';
    if (/gatt server is disconnected/i.test(msg)) return true;
    if (/early disconnect detected/i.test(msg)) return true;
    if (/step timeout:/i.test(msg)) return true;
    return false;
  }

  async disconnect(): Promise<void> {
    try {
      if (this.device?.gatt?.connected) {
        this.device.gatt.disconnect();
      }
      this.device = null;
      this.characteristic = null;
      this.isConnected = false;

      // 연결 해제 시 버퍼/상태 초기화
      this.clearBuffer();
      this.mode = 'single';
      this.frameParser.setMode('single');
    } catch (error) {
      console.error('[BLE]fail(disconnect):', error);
      throw error;
    }
  }

  getConnectionStatus(): boolean {
    // 내부 상태와 실제 GATT 연결 상태 모두 확인
    const gattConnected = this.device?.gatt?.connected || false;
    return this.isConnected && gattConnected;
  }

  getSelectedDeviceName(): string {
    return this.selectedDeviceName;
  }

  private handleNotification(event: Event): void {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value;
    if (!value) return;

    const data = new Uint8Array(value.buffer);
    this.notificationCount++;
    this.frameParser.pushBytes(data);

    while (true) {
      const packet = this.frameParser.extractPacket();
      if (!packet) break;
      if (this.mode === 'single') {
        this.dispatchSinglePacket(packet);
      } else {
        // STREAM 모드: 패킷을 큐에 추가 (대기 중인 shift()를 즉시 해제)
        this.packetQueue.push(packet);
      }
    }
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.characteristic) {
      throw new I18nError(i18nKeys.config.error_bleCharacteristicNotAvailable);
    }

    try {
      // SINGLE 모드에서만 버퍼 초기화 + 응답 대기 준비.
      // STREAM 모드에서는 버퍼를 유지하여 이미 수신된 데이터를 보존
      if (this.mode === 'single') {
        this.frameParser.clear();
        this.armPendingResponse();
      }

      const maxChunkSize = 20;

      if (data.length > maxChunkSize) {
        for (let i = 0; i < data.length; i += maxChunkSize) {
          const chunk = data.slice(i, i + maxChunkSize);
          // writeValueWithoutResponse가 내부적으로 flow control을 하므로 추가 딜레이 불필요
          await this.characteristic.writeValueWithoutResponse(chunk.buffer as ArrayBuffer);
        }
      } else {
        await this.characteristic.writeValueWithoutResponse(data.buffer as ArrayBuffer);
      }
    } catch (error) {
      console.error('[BLE] fail(write):', error);
      throw error;
    }
  }

  isOpen(): boolean {
    return this.isConnected && this.characteristic !== null;
  }
}
